import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  applyTypography,
  BaseButton,
  Modal,
  useAuth,
  LoginRequiredModal,
  trackCtaClick,
  trackWorkshopBookComplete,
  trackWorkshopBookStart,
} from '@repo/common';
import { ReactComponent as ShoppingBagIcon32 } from '@repo/common/assets/icons/32px/icon_32px_shopping-bag.svg';
import { ReactComponent as ShoppingBagFilledIcon32 } from '@repo/common/assets/icons/32px/icon_32px_shopping-bag_filled_on.svg';
import { ReactComponent as HeartOutlineIcon32 } from '@repo/common/assets/icons/32px/icon_32px_heart_outline.svg';
import { ReactComponent as HeartFilledIcon32 } from '@repo/common/assets/icons/32px/icon_32px_heart_filled.svg';
import { ReactComponent as InfoIcon20 } from '@repo/common/assets/icons/20px/icon_20px_info_filled.svg';
import { ReactComponent as WarningIcon20 } from '@repo/common/assets/icons/20px/icon_20px_warning_filled.svg';
import { ReactComponent as EditIcon16 } from '@repo/common/assets/icons/16px/icon_16px_edit_outline.svg';
import { ReactComponent as DeleteIcon20 } from '@repo/common/assets/icons/20px/icon_20px_delete_outline.svg';
import ImageGallery from './ImageGallery';
import WorkshopInfo from './WorkshopInfo';
import TabSection from './TabSection';
import LocationSection from './LocationSection';
import MVPSection from './MVPSection';
import ThingsToKnowSection from './ThingsToKnowSection';
import StickyBookingBar from './StickyBookingBar';
import BookingConflictModal from '../Modal/BookingConflictModal';
import SuccessNotificationToast from '../Modal/SuccessNotificationModal';
import WaitlistSuccessModal from '../Modal/WaitlistSuccessModal';
import AlreadyInBagModal from '../Modal/AlreadyInBagModal';
import ItemUnavailableModal from '../Modal/ItemUnavailableModal';
import DateSelectionModal from '../DateSelectionModal';
import IntakeQuestionsModal from '../Modal/IntakeQuestionsModal';
import useIntakeGate from '../hooks/useIntakeGate';
import {
  buildAnswersPayload,
  clearIntakeAnswers,
} from '../utils/intakeAnswers';
import { addToCart, getCartItems } from '../../../api/cart/cart.api';
import {
  formatWorkshopPrice,
  isBankTransferMethod,
} from '../utils/priceUtils';
import {
  createBookings,
  getUserBookings,
} from '../../../api/booking/booking.api';
import {
  addWorkshopLike,
  getWorkshopLikeStatus,
  removeWorkshopLike,
} from '../../../api/workshop/workshopLike.api';
import { deleteWorkshop } from '../../../api/workshop/workshop.api';
import { getWorkshopReviews } from '../../../api/workshop/workshopReview.api';

const formatBlockReleaseDate = (value, includeYear = false) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(includeYear ? { year: 'numeric' } : {}),
  });
};

const BlockedWorkshopBanner = ({ releaseDateText }) => (
  <div className='flex min-h-[44px] w-full items-center gap-[8px] rounded-[8px] border border-functional-warning03 bg-functional-warning01 px-[16px] py-[10px] tabletH:min-h-[84px] tabletH:px-[24px]'>
    <WarningIcon20 className='size-[20px] shrink-0 text-icon-warning' />
    <div className='flex flex-col justify-center min-w-0'>
      <p
        className={`${applyTypography(
          'b12Strong',
        )} text-text-warning whitespace-pre-wrap`}
        style={{ lineHeight: '1.4' }}
      >
        You are currently restricted from booking this workshop.
      </p>
      <p
        className={`${applyTypography(
          'b12Strong',
        )} text-text-warning whitespace-pre-wrap`}
        style={{ lineHeight: '1.4' }}
      >
        {releaseDateText
          ? `Restriction ends on ${releaseDateText}.`
          : 'Restriction end date is unavailable.'}
      </p>
    </div>
  </div>
);

const WorkshopDetail = ({ workshop = {} }) => {
  const navigate = useNavigate();
  // 예약 전 사전 질문 게이트 — 질문이 없는 워크숍은 그냥 통과한다
  const intakeGate = useIntakeGate();
  // 후기는 Reviews 탭에서만 쓰므로 상세와 따로 받는다
  const [reviewSummary, setReviewSummary] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isInBag, setIsInBag] = useState(false);
  const [selectedDateSlot, setSelectedDateSlot] = useState(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [showDateWarning, setShowDateWarning] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [showAddedToBagToast, setShowAddedToBagToast] = useState(false);
  const [successToastType, setSuccessToastType] = useState('bag');
  const [showWaitlistSuccessModal, setShowWaitlistSuccessModal] =
    useState(false);
  const [showAlreadyInBagModal, setShowAlreadyInBagModal] = useState(false);
  const [showItemUnavailableModal, setShowItemUnavailableModal] =
    useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLikeLoading, setIsLikeLoading] = useState(false);
  const [showDeleteWorkshopModal, setShowDeleteWorkshopModal] = useState(false);
  const [isDeletingWorkshop, setIsDeletingWorkshop] = useState(false);
  const [slotStatusMap, setSlotStatusMap] = useState({});
  const [hasBookedWorkshop, setHasBookedWorkshop] = useState(false);
  const [hasPendingPaymentWorkshop, setHasPendingPaymentWorkshop] =
    useState(false);
  const workshopInfoRef = useRef(null);
  const { isLoggedIn, user } = useAuth();
  const isAdmin = ['A', 'ADMIN', 'ROLE_A'].includes(
    String(user?.role ?? '').toUpperCase(),
  );

  const handleDeleteWorkshop = async () => {
    if (!workshop?.id || isDeletingWorkshop) return;

    try {
      setIsDeletingWorkshop(true);
      await deleteWorkshop(workshop.id);
      navigate('/community/kpathport/pathfeed', {
        replace: true,
        state: { workshopDeleted: true },
      });
    } catch (error) {
      console.error('Failed to delete workshop:', error);
      window.alert(
        error.response?.data?.msg ||
          error.response?.data?.message ||
          'The workshop could not be deleted. Please try again.',
      );
    } finally {
      setIsDeletingWorkshop(false);
      setShowDeleteWorkshopModal(false);
    }
  };

  // 후기 목록 — 작성은 마이페이지에서 하므로 이 화면은 진입할 때 한 번만 받는다
  useEffect(() => {
    if (!workshop?.id) return undefined;

    let active = true;
    setReviewsLoading(true);
    getWorkshopReviews(workshop.id)
      .then((data) => {
        if (active) setReviewSummary(data);
      })
      .catch(() => {
        // 후기를 못 불러와도 상세 페이지는 그대로 보여준다
        if (active) setReviewSummary(null);
      })
      .finally(() => {
        if (active) setReviewsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [workshop?.id]);

  // 장바구니에 현재 워크샵이 있는지 확인
  useEffect(() => {
    const checkCartStatus = async () => {
      if (!workshop?.id) return;

      try {
        const cartItems = await getCartItems();
        // 현재 워크샵의 날짜 슬롯이 장바구니에 있는지 확인
        const isInCart = cartItems.some(
          (item) => item.workshopId === workshop.id,
        );
        setIsInBag(isInCart);
        setSuccessToastType('bag');
      } catch (error) {
        console.error('Failed to check cart status:', error);
      }
    };

    checkCartStatus();
  }, [workshop?.id]);

  useEffect(() => {
    const fetchLikeStatus = async () => {
      if (!workshop?.id) return;
      if (!isLoggedIn) {
        setIsWishlisted(false);
        return;
      }
      try {
        const liked = await getWorkshopLikeStatus(workshop.id);
        setIsWishlisted(!!liked);
      } catch (error) {
        console.error('Failed to fetch like status:', error);
      }
    };

    fetchLikeStatus();
  }, [isLoggedIn, workshop.id]);

  useEffect(() => {
    const handleScroll = () => {
      if (workshopInfoRef.current) {
        const rect = workshopInfoRef.current.getBoundingClientRect();
        // WorkshopInfo가 뷰포트를 완전히 벗어났는지 확인
        setShowStickyBar(rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // 초기 상태 확인

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleWishlistToggle = async () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    if (!workshop?.id || isLikeLoading) return;

    setIsLikeLoading(true);
    try {
      if (isWishlisted) {
        await removeWorkshopLike(workshop.id);
        setIsWishlisted(false);
      } else {
        await addWorkshopLike(workshop.id);
        setIsWishlisted(true);
      }
    } catch (error) {
      console.error('Failed to toggle wishlist:', error);
    } finally {
      setIsLikeLoading(false);
    }
  };

  // price가 0이면 Free. 통화별 표기는 priceUtils 가 담당 (KRW → "55,000won", USD → "15.00$")
  const formatPrice = (price, isFree = false) =>
    formatWorkshopPrice(price, workshop.currency, isFree);

  const formattedPrice = formatPrice(workshop.price, workshop.free);
  const isBankTransferWorkshop = isBankTransferMethod(workshop.paymentMethod);
  const isBlocked = Boolean(workshop?.isBlocked);
  const blockReleaseAt =
    workshop?.unblockedAt ||
    workshop?.blockedUntil ||
    workshop?.blockReleaseAt ||
    workshop?.blockReleaseDate ||
    '';
  const blockReleaseShortText = formatBlockReleaseDate(blockReleaseAt);
  const blockReleaseLongText = formatBlockReleaseDate(blockReleaseAt, true);
  const blockedPrimaryText = blockReleaseShortText
    ? `Available after ${blockReleaseShortText}`
    : 'Currently unavailable';
  const blockedStickyText = blockReleaseShortText
    ? `After ${blockReleaseShortText}`
    : 'Unavailable';
  const displayDateSlots = useMemo(
    () => (Array.isArray(workshop?.dateSlots) ? workshop.dateSlots : []),
    [workshop?.dateSlots],
  );

  useEffect(() => {
    if (!isLoggedIn || !workshop?.id || displayDateSlots.length === 0) {
      setSlotStatusMap({});
      setHasBookedWorkshop(false);
      setHasPendingPaymentWorkshop(false);
      return;
    }

    let cancelled = false;

    const syncStatuses = async () => {
      try {
        const slotIds = new Set(
          displayDateSlots.map((slot) => String(slot.id)),
        );
        const [cartResult, bookingsResult] = await Promise.allSettled([
          getCartItems(),
          getUserBookings(),
        ]);
        const cartItems =
          cartResult.status === 'fulfilled' ? cartResult.value : [];
        const bookings =
          bookingsResult.status === 'fulfilled' ? bookingsResult.value : [];

        const nextMap = {};
        let hasBooked = false;
        let hasPendingPayment = false;

        (cartItems || []).forEach((item) => {
          const slotId = String(
            item?.workshopDateSlotId ?? item?.workshopDateSlot?.id,
          );
          if (slotIds.has(slotId)) {
            nextMap[slotId] = 'inBag';
          }
        });

        (bookings || []).forEach((booking) => {
          const bookingWorkshopId = String(
            booking?.workshopId ?? booking?.workshop?.id ?? '',
          );
          const slotId = String(
            booking?.workshopDateSlotId ??
              booking?.workshopDateSlot?.id ??
              booking?.dateSlotId,
          );
          const status = String(
            booking?.status ?? booking?.bookingStatus ?? '',
          ).toUpperCase();
          const isWaitlist =
            status.includes('WAITLIST') || status.includes('WAITING');
          const isPendingPayment =
            status.includes('HOLD') || status.includes('OFFERED');
          const isConfirmed = status.includes('CONFIRMED');
          const isCanceled =
            status.includes('CANCEL') ||
            status.includes('EXPIRED') ||
            status.includes('REFUND');

          if (bookingWorkshopId === String(workshop.id) && !isCanceled) {
            if (isConfirmed) {
              hasBooked = true;
            }
            if (isPendingPayment) {
              hasPendingPayment = true;
            }
          }

          if (!slotIds.has(slotId)) return;
          if (isWaitlist) {
            nextMap[slotId] = 'onWaitlist';
          } else if (isPendingPayment) {
            nextMap[slotId] = 'inBag';
          } else if (isConfirmed) {
            nextMap[slotId] = 'booked';
          }
        });

        if (!cancelled) {
          setSlotStatusMap(nextMap);
          setHasBookedWorkshop(hasBooked);
          setHasPendingPaymentWorkshop(hasPendingPayment);
        }
      } catch (error) {
        console.error('Failed to sync workshop slot statuses:', error);
      }
    };

    syncStatuses();

    return () => {
      cancelled = true;
    };
  }, [displayDateSlots, isLoggedIn, workshop?.id]);

  const displayDateSlotsWithStatus = useMemo(() => {
    return displayDateSlots.map((slot) => ({
      ...slot,
      workshopStatus: slotStatusMap[String(slot.id)] || slot.workshopStatus,
    }));
  }, [displayDateSlots, slotStatusMap]);

  const selectedSlot = useMemo(() => {
    return displayDateSlotsWithStatus.find(
      (slot) => String(slot.id) === String(selectedDateSlot),
    );
  }, [displayDateSlotsWithStatus, selectedDateSlot]);

  // 슬롯의 종료 시각이 현재 시각보다 이전인지 확인
  const isSlotPast = (slot) => {
    if (!slot) return false;
    if (slot.isPast === true) return true;
    // 백엔드 isPast가 없을 때 rawDate + endTime으로 직접 계산
    if (slot.rawDate) {
      const [year, month, day] = slot.rawDate.split('-').map(Number);
      const endHour = slot.endTime
        ? parseInt(slot.endTime.split(':')[0], 10)
        : 23;
      const endMinute = slot.endTime
        ? parseInt(slot.endTime.split(':')[1], 10)
        : 59;
      const slotEnd = new Date(year, month - 1, day, endHour, endMinute, 0, 0);
      return slotEnd < new Date();
    }
    return false;
  };

  const inferWorkshopStatus = (slot) => {
    if (!slot) return 'available';
    if (slot.workshopStatus) return slot.workshopStatus;
    const dateSlotStatus = String(slot.dateSlotStatus || '').toLowerCase();
    if (dateSlotStatus === 'ongoing') return 'ongoing';
    if (dateSlotStatus === 'closed') return 'closed';
    if (dateSlotStatus === 'completed') return 'completed';

    const normalizedStatus = String(slot.status || '').toLowerCase();
    if (normalizedStatus.includes('bag')) return 'inBag';
    if (normalizedStatus.includes('booked')) return 'booked';
    if (
      normalizedStatus.includes('pending') ||
      normalizedStatus.includes('hold') ||
      normalizedStatus.includes('offered')
    ) {
      return 'inBag';
    }
    if (
      normalizedStatus.includes('waitlist') ||
      normalizedStatus.includes('wait list') ||
      normalizedStatus.includes('waiting')
    ) {
      return 'onWaitlist';
    }
    if (slot.available === false || slot.status === 'Full') return 'full';
    return 'available';
  };

  const primaryButtonState = useMemo(() => {
    if (isBlocked) {
      return { type: 'blocked', text: blockedPrimaryText, disabled: true };
    }
    if (hasBookedWorkshop) {
      return { type: 'booked', text: 'Booked', disabled: true };
    }
    if (hasPendingPaymentWorkshop) {
      return {
        type: 'inBag',
        text: 'In your bag',
        disabled: true,
      };
    }
    if (!selectedSlot) {
      return { type: 'bookNow', text: 'Book now', disabled: true };
    }

    const status = inferWorkshopStatus(selectedSlot);
    if (status === 'inBag') {
      return { type: 'inBag', text: 'In your bag', disabled: true };
    }
    if (status === 'onWaitlist') {
      return { type: 'onWaitlist', text: 'On waitlist', disabled: true };
    }
    if (status === 'booked') {
      return { type: 'booked', text: 'Booked', disabled: true };
    }
    if (status === 'ongoing' || status === 'closed' || status === 'completed') {
      return { type: status, text: 'Book now', disabled: true };
    }
    if (status === 'full') {
      return { type: 'joinWaitlist', text: 'Join waitlist', disabled: false };
    }

    return { type: 'bookNow', text: 'Book now', disabled: false };
  }, [
    blockedPrimaryText,
    hasBookedWorkshop,
    hasPendingPaymentWorkshop,
    isBlocked,
    selectedSlot,
  ]);

  // 장바구니 버튼 — 항상 추가만 시도 (이동 없음)
  const handleShoppingBagClick = async () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    if (isBlocked) return;
    if (hasBookedWorkshop) return;
    if (!selectedDateSlot) {
      setShowDateWarning(true);
      setTimeout(() => setShowDateWarning(false), 3000);
      return;
    }

    const slot = displayDateSlotsWithStatus.find(
      (s) => s.id === selectedDateSlot,
    );
    if (inferWorkshopStatus(slot) === 'booked') return;

    if (isSlotPast(slot)) {
      setShowItemUnavailableModal(true);
      return;
    }

    try {
      await addToCart(slot.id, 1);
      setSlotStatusMap((prev) => ({ ...prev, [String(slot.id)]: 'inBag' }));
      setIsInBag(true);
      setSuccessToastType('bag');
      setShowAddedToBagToast(true);
    } catch (error) {
      const statusCode = error.response?.status;
      const errorCode = error.response?.data?.code;
      const errorMessage =
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.message;

      const isAlreadyInBag =
        statusCode === 409 ||
        errorCode === 409 ||
        errorMessage?.includes('already exists') ||
        errorMessage?.includes('CART_ITEM_ALREADY_EXISTS');

      if (isAlreadyInBag) {
        setSlotStatusMap((prev) => ({ ...prev, [String(slot.id)]: 'inBag' }));
        setIsInBag(true);
        setSuccessToastType('alreadyInBag');
        setShowAddedToBagToast(true);
        return;
      }

      if (
        errorCode === 'ITEM_UNAVAILABLE' ||
        errorMessage?.includes('no longer available') ||
        errorMessage?.includes('unavailable')
      ) {
        setShowItemUnavailableModal(true);
        return;
      }

      alert(errorMessage || 'Failed to add to cart. Please try again.');
    }
  };

  /**
   * 사전 질문이 있으면 먼저 받고 결제/무료예약으로 넘어간다.
   * 질문이 없으면 게이트가 그대로 통과시킨다.
   */
  const moveToPaymentForSlot = async (workshopDateSlotId, fallbackCartItemId) =>
    intakeGate.run(workshop, () =>
      proceedToPaymentForSlot(workshopDateSlotId, fallbackCartItemId),
    );

  const proceedToPaymentForSlot = async (
    workshopDateSlotId,
    fallbackCartItemId,
  ) => {
    const cartItems = await getCartItems();
    const isInCart = cartItems.some((item) => item.workshopId === workshop.id);
    setIsInBag(isInCart);

    const matchedCartItem = cartItems.find(
      (item) => String(item?.workshopDateSlotId) === String(workshopDateSlotId),
    );
    const cartItemId = fallbackCartItemId || matchedCartItem?.id;

    if (!cartItemId) {
      alert('Failed to find cart item for payment. Please try again.');
      return;
    }

    const isFreeWorkshop =
      Boolean(workshop?.free) || Number(workshop?.price ?? 0) <= 0;

    if (isFreeWorkshop) {
      const customerName =
        user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`
          : user?.nickname || user?.email || '';
      const customerEmail = user?.email || user?.socialEmail || '';

      const bookingIds = await createBookings(
        [cartItemId],
        customerName,
        customerEmail,
        { answers: buildAnswersPayload([workshop.id]) },
      );

      if (!Array.isArray(bookingIds) || bookingIds.length === 0) {
        alert('Failed to complete free booking. Please try again.');
        return;
      }

      clearIntakeAnswers([workshop.id]);
      navigate(
        `/community/kpathport/confirmed?bookingIds=${bookingIds.join(',')}`,
        { replace: true },
      );
      return;
    }

    navigate(`/community/kpathport/payments?cartItemIds=${cartItemId}`);
  };

  const handleJoinWaitlist = async (slot) => {
    if (!slot) return;
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    if (hasBookedWorkshop) {
      setShowConflictModal(true);
      return;
    }
    // 대기열도 같은 예약 행이 되므로 사전 질문을 먼저 받는다
    await intakeGate.run(workshop, () => proceedToJoinWaitlist(slot));
  };

  const proceedToJoinWaitlist = async (slot) => {
    try {
      // waitlist는 곧바로 cart item이 정리되므로 중간 장바구니 상태를 동기화하지 않는다.
      const addedCartItem = await addToCart(slot.id, 1, {
        skipCartStatusSync: true,
      });
      let cartItemId = addedCartItem?.id ?? addedCartItem?.cartItemId;

      if (!cartItemId) {
        const cartItems = await getCartItems();
        const matched = (cartItems || []).find(
          (item) => String(item?.workshopDateSlotId) === String(slot.id),
        );
        cartItemId = matched?.id;
      }

      if (!cartItemId) {
        throw new Error('Failed to resolve cart item for waitlist');
      }

      const customerName =
        user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`
          : user?.nickname || user?.email || '';
      const customerEmail = user?.email || '';

      await createBookings([cartItemId], customerName, customerEmail, {
        answers: buildAnswersPayload([workshop.id]),
      });
      clearIntakeAnswers([workshop.id]);

      trackWorkshopBookComplete({
        workshop_id: selectedSlot.workshopId,
        workshop_name: selectedSlot.workshopTitle,
        workshop_date: selectedSlot.date,
        booking_status: 'waitlist',
        step_number: selectedSlot.stepNumber,
      });
      // cart item은 백엔드 createBookingsFromCart에서 WAITLISTED 처리와 함께 삭제된다.

      setSlotStatusMap((prev) => ({
        ...prev,
        [String(slot.id)]: 'onWaitlist',
      }));
      setIsInBag(false);
      setShowWaitlistSuccessModal(true);
    } catch (error) {
      console.error('Failed to join waitlist:', error);
      const errorCode = error?.response?.data?.code;
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        error?.message ||
        '';
      const lowerMessage = String(errorMessage).toLowerCase();

      if (
        String(errorCode).includes('ALREADY') ||
        String(errorCode).includes('EXISTS') ||
        lowerMessage.includes('already')
      ) {
        setSlotStatusMap((prev) => ({
          ...prev,
          [String(slot.id)]: 'onWaitlist',
        }));
        setShowWaitlistSuccessModal(true);
        return;
      }

      alert(errorMessage || 'Failed to join waitlist. Please try again.');
    }
  };

  const handleBookNow = async () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    if (isBlocked) return;
    if (!selectedSlot) {
      setShowDateWarning(true);
      setTimeout(() => {
        setShowDateWarning(false);
      }, 3000);
      return;
    }
    if (hasBookedWorkshop) return;

    // 이미 지난 날짜/시간이면 결제창 진입 차단
    if (isSlotPast(selectedSlot)) {
      setShowItemUnavailableModal(true);
      return;
    }

    const workshopStatus = inferWorkshopStatus(selectedSlot);
    if (workshopStatus === 'full') {
      await handleJoinWaitlist(selectedSlot);
      return;
    }
    if (workshopStatus === 'inBag' || workshopStatus === 'onWaitlist') {
      return;
    }
    if (workshopStatus === 'booked') {
      return;
    }
    if (
      workshopStatus === 'ongoing' ||
      workshopStatus === 'closed' ||
      workshopStatus === 'completed'
    ) {
      return;
    }

    try {
      const addedCartItem = await addToCart(selectedSlot.id, 1);
      const addedCartItemId = addedCartItem?.id ?? addedCartItem?.cartItemId;
      await moveToPaymentForSlot(selectedSlot.id, addedCartItemId);
      trackBookingStart();
    } catch (error) {
      console.error('Failed to add to cart (handleBookNow):', error);
      const errorCode = error.response?.data?.code;
      const errorMessage =
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.message;
      const statusCode = error.response?.status;

      const isAlreadyInBag =
        statusCode === 409 ||
        errorCode === 409 ||
        errorMessage?.includes('already exists') ||
        errorMessage?.includes('CART_ITEM_ALREADY_EXISTS');

      if (isAlreadyInBag) {
        if (selectedSlot) {
          await moveToPaymentForSlot(selectedSlot.id);
          return;
        }
        setShowAlreadyInBagModal(true);
        return;
      }

      if (
        errorCode === 'ITEM_UNAVAILABLE' ||
        errorMessage?.includes('no longer available') ||
        errorMessage?.includes('unavailable')
      ) {
        setShowItemUnavailableModal(true);
        return;
      }

      alert(errorMessage || 'Failed to proceed to payment. Please try again.');
    }
  };

  const trackBookingStart = () => {
    const stepNumber =
      parseInt(String(workshop.step).replace(/\D/g, ''), 10) || null;

    trackWorkshopBookStart({
      workshop_id: workshop.id,
      workshop_name: workshop.title,
      step_number: stepNumber,
      step_name: workshop.step,
      source: 'detail_page_cta',
    });
  };

  const handleAddToCart = async () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    if (isBlocked) return;
    if (hasBookedWorkshop) return;
    if (!selectedDateSlot) {
      setShowDateWarning(true);
      setTimeout(() => {
        setShowDateWarning(false);
      }, 3000);
      return;
    }

    try {
      // 선택된 날짜 슬롯 ID로 장바구니에 추가
      const selectedSlot = workshop.dateSlots?.find(
        (slot) => slot.id === selectedDateSlot,
      );
      if (selectedSlot) {
        // 이미 지난 날짜/시간이면 차단
        if (isSlotPast(selectedSlot)) {
          setShowItemUnavailableModal(true);
          return;
        }
        if (inferWorkshopStatus(selectedSlot) === 'booked') {
          return;
        }
        console.log('Adding to cart - workshopDateSlotId:', selectedSlot.id);
        await addToCart(selectedSlot.id, 1);
        setSlotStatusMap((prev) => ({
          ...prev,
          [String(selectedSlot.id)]: 'inBag',
        }));
        setIsInBag(true);
        // 장바구니 상태 다시 확인
        const cartItems = await getCartItems();
        const isInCart = cartItems.some(
          (item) => item.workshopId === workshop.id,
        );
        setIsInBag(isInCart);
        // 성공 토스트 표시
        setSuccessToastType('bag');
        setShowAddedToBagToast(true);
      } else {
        alert('선택된 날짜 슬롯을 찾을 수 없습니다.');
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error message:', error.message);

      // 중복 에러인 경우 모달 표시
      const errorCode = error.response?.data?.code; // 숫자 코드 (예: 409)
      const errorMessage =
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.message;
      const statusCode = error.response?.status; // HTTP 상태 코드

      // 409 상태 코드이거나 에러 메시지에 "이미 장바구니에 담긴"이 포함된 경우
      if (
        statusCode === 409 ||
        errorCode === 409 ||
        errorMessage?.includes('이미 장바구니에 담긴') ||
        errorMessage?.includes('already exists') ||
        errorMessage?.includes('CART_ITEM_ALREADY_EXISTS')
      ) {
        if (selectedDateSlot) {
          setSlotStatusMap((prev) => ({
            ...prev,
            [String(selectedDateSlot)]: 'inBag',
          }));
        }
        setIsInBag(true);
        setShowAlreadyInBagModal(true);
        return;
      }

      // 시스템/타이밍 에러인 경우
      if (
        errorCode === 'ITEM_UNAVAILABLE' ||
        errorMessage?.includes('no longer available') ||
        errorMessage?.includes('사용 불가능') ||
        errorMessage?.includes('unavailable')
      ) {
        setShowItemUnavailableModal(true);
        return;
      }

      // 기타 에러는 alert로 표시
      alert(
        errorMessage ||
          '장바구니에 추가하는데 실패했습니다. 다시 시도해주세요.',
      );
    }
  };

  const handleConflictModalContinue = () => {
    // Continue 버튼 클릭 시 모달만 닫기 (중복 추가는 허용하지 않음)
    setShowConflictModal(false);
  };

  // StickyBookingBar용 Book now 핸들러 (날짜 선택 모달 열기)
  const handleStickyBookNow = () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    if (isBlocked) return;
    if (selectedSlot) {
      handleBookNow();
      return;
    }
    setShowDateModal(true);
  };

  const handleViewBooking = () => {
    navigate('/mypage?tab=Activities');
  };

  const handleViewWaitlist = () => {
    navigate('/mypage?tab=Activities&workshopFilter=Waitlist');
  };

  // 날짜 선택 후 처리
  const handleDateSelected = async (workshopDateSlotId) => {
    if (isBlocked) return;
    if (hasBookedWorkshop) return;
    // 이미 지난 날짜/시간이면 차단
    const targetSlot = displayDateSlotsWithStatus.find(
      (s) => String(s.id) === String(workshopDateSlotId),
    );
    if (isSlotPast(targetSlot)) {
      setShowDateModal(false);
      setShowItemUnavailableModal(true);
      return;
    }
    try {
      const addedCartItem = await addToCart(workshopDateSlotId, 1);
      const addedCartItemId = addedCartItem?.id ?? addedCartItem?.cartItemId;
      setSelectedDateSlot(workshopDateSlotId);
      setShowDateModal(false);
      await moveToPaymentForSlot(workshopDateSlotId, addedCartItemId);
    } catch (error) {
      console.error('Failed to add to cart (handleDateSelected):', error);
      const errorCode = error.response?.data?.code;
      const errorMessage =
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.message;
      const statusCode = error.response?.status;

      const isAlreadyInBag =
        statusCode === 409 ||
        errorCode === 409 ||
        errorMessage?.includes('already exists') ||
        errorMessage?.includes('CART_ITEM_ALREADY_EXISTS');

      if (isAlreadyInBag) {
        setShowDateModal(false);
        await moveToPaymentForSlot(workshopDateSlotId);
        return;
      }

      if (
        errorCode === 'ITEM_UNAVAILABLE' ||
        errorMessage?.includes('no longer available') ||
        errorMessage?.includes('unavailable')
      ) {
        setShowDateModal(false);
        setShowItemUnavailableModal(true);
        return;
      }

      alert(errorMessage || 'Failed to proceed to payment. Please try again.');
    }
  };
  // 선택된 날짜 슬롯에서 availability 가져오기
  const getAvailability = () => {
    const slots =
      displayDateSlotsWithStatus.length > 0
        ? displayDateSlotsWithStatus
        : [
            {
              id: 1,
              date: 'Fri ∙ 5 Dec 2025',
              time: '18:00 - 20:00PM',
              status: 'Full',
            },
            {
              id: 2,
              date: 'Fri ∙ 5 Dec 2025',
              time: '18:00 - 20:00PM',
              status: '1 left',
            },
            {
              id: 3,
              date: 'Fri ∙ 5 Dec 2025',
              time: '18:00 - 20:00PM',
              status: '3 left',
            },
            {
              id: 4,
              date: 'Fri ∙ 5 Dec 2025',
              time: '18:00 - 20:00PM',
              status: '6 left',
            },
            {
              id: 5,
              date: 'Fri ∙ 5 Dec 2025',
              time: '18:00 - 20:00PM',
              status: '6 left',
            },
          ];
    const currentSelectedSlot = slots.find(
      (slot) => slot.id === selectedDateSlot,
    );
    return (
      currentSelectedSlot?.status ||
      slots.find((slot) => slot.status !== 'Full')?.status ||
      '1 left'
    );
  };

  const isBookNowDisabled = primaryButtonState.disabled;
  const primaryButtonText = primaryButtonState.text;
  const isPrimaryBlocked = primaryButtonState.type === 'blocked';
  const isPrimaryBooked = primaryButtonState.type === 'booked';
  const isPrimaryInBag = primaryButtonState.type === 'inBag';
  const isPrimaryOnWaitlist = primaryButtonState.type === 'onWaitlist';
  const isPrimaryJoinWaitlist = primaryButtonState.type === 'joinWaitlist';
  const isPrimaryDateUnavailable =
    primaryButtonState.type === 'ongoing' ||
    primaryButtonState.type === 'closed' ||
    primaryButtonState.type === 'completed';
  const isCartDisabled =
    isBlocked ||
    isPrimaryBooked ||
    isPrimaryOnWaitlist ||
    isPrimaryDateUnavailable;
  const selectedSlotGuideText = {
    ongoing:
      'This workshop is currently in progress. Please select another date.',
    closed: 'This date is fully booked. Please select another date.',
    completed: 'This workshop has ended. Please select another date.',
  }[primaryButtonState.type];
  const isStickyBookNowDisabled =
    isPrimaryBlocked || isPrimaryInBag || isPrimaryDateUnavailable;
  const stickyBookNowText = isPrimaryBlocked
    ? blockedStickyText
    : isPrimaryBooked
      ? 'View Booking'
      : isPrimaryOnWaitlist
        ? 'View Waitlist'
        : isStickyBookNowDisabled
          ? primaryButtonText
          : 'Book now';
  const stickyBookNowSubText = isPrimaryBooked
    ? 'Already booked'
    : isPrimaryOnWaitlist
      ? 'Already on waitlist'
      : '';

  return (
    <div className='flex flex-col items-start pb-[240px] pt-[16px] relative w-full bg-grayscale-gray01 tabletH:pt-[80px] tabletH:px-[40px] tabletV:px-[80px]'>
      <IntakeQuestionsModal {...intakeGate.modalProps} />
      {isAdmin && (
        <div className='flex w-full justify-end gap-2 px-6 pb-4 tabletH:px-0 tabletH:pb-6'>
          <BaseButton
            type='text'
            size='m'
            typo={false}
            icon={<EditIcon16 className='size-4' />}
            onClick={() => navigate(`/admin/workshop/${workshop.id}/edit`)}
            className='h-10 min-w-0 rounded-lg border border-grayscale-gray03 bg-white px-4 typo-b14Strong text-text-primary hover:bg-grayscale-gray02 hover:text-text-primary'
          >
            Edit
          </BaseButton>
          <BaseButton
            type='text'
            size='m'
            typo={false}
            icon={<DeleteIcon20 className='size-5' />}
            onClick={() => setShowDeleteWorkshopModal(true)}
            className='h-10 min-w-0 rounded-lg border border-functional-failed03 bg-white px-4 typo-b14Strong text-functional-failed03 hover:bg-functional-failed01 hover:text-functional-failed03'
          >
            Delete
          </BaseButton>
        </div>
      )}

      {/* Main Content Section */}
      <div className='flex flex-col gap-0 items-start pb-[60px] pt-0 px-6 relative w-full tabletH:px-0 tabletH:flex-wrap tabletH:flex-row tabletH:gap-[48px] tabletH:pb-[80px]'>
        {/* Workshop Info */}
        <div
          ref={workshopInfoRef}
          className={`order-1 flex w-full flex-col items-start min-h-px pb-[16px] pt-0 px-0 relative shrink-0 tabletH:order-2 tabletH:flex-[1_0_0] tabletH:min-w-[421px] tabletH:pb-[60px] ${
            isBlocked ? 'tabletH:pt-0' : 'tabletH:pt-[20px]'
          }`}
        >
          {isBlocked && (
            <div className='w-full pb-[16px] tabletH:pb-[20px] tabletH:pr-[12px]'>
              <BlockedWorkshopBanner releaseDateText={blockReleaseLongText} />
            </div>
          )}
          <WorkshopInfo
            step={workshop.step}
            title={workshop.title}
            dateSlots={displayDateSlotsWithStatus}
            location={workshop.location}
            price={formattedPrice}
            currency={workshop.currency}
            description={workshop.description}
            selectedDateSlot={selectedDateSlot}
            onSelectDateSlot={setSelectedDateSlot}
            isBlocked={isBlocked}
            paymentNote={
              isBankTransferWorkshop
                ? 'Bank transfer only · Your booking is confirmed once payment is received'
                : ''
            }
            middleContent={
              <div className='tabletH:hidden flex w-full flex-col gap-[8px] items-start pb-[24px] relative shrink-0'>
                <ImageGallery images={workshop.images} />
              </div>
            }
          />

          {/* Action Buttons */}
          <div className='flex flex-col gap-[8px] items-start px-0 py-0 relative shrink-0 w-full tabletH:pr-[400px]'>
            {showDateWarning && (
              <div className='flex gap-[2px] items-center pb-[8px] relative shrink-0'>
                <div className='overflow-clip relative shrink-0 size-[20px]'>
                  <InfoIcon20 className='w-full h-full text-functional-failed03' />
                </div>
                <p
                  className={`${applyTypography(
                    'b14Strong',
                  )} text-functional-failed03 shrink-0`}
                  style={{ lineHeight: '1.4' }}
                >
                  Select a workshop date before booking
                </p>
              </div>
            )}
            {selectedSlotGuideText && (
              <div className='flex gap-[2px] items-start pb-[8px] relative shrink-0 w-full'>
                <div className='overflow-clip relative shrink-0 size-[20px]'>
                  <InfoIcon20 className='w-full h-full text-grayscale-gray05' />
                </div>
                <p
                  className={`${applyTypography(
                    'b14Strong',
                  )} text-grayscale-gray05 min-w-0 shrink`}
                  style={{ lineHeight: '1.4' }}
                >
                  {selectedSlotGuideText}
                </p>
              </div>
            )}
            <div className='flex gap-[8px] items-center relative shrink-0 w-full tabletH:gap-[16px]'>
              <BaseButton
                type={
                  isPrimaryOnWaitlist || isPrimaryJoinWaitlist
                    ? 'secondary'
                    : 'primary'
                }
                size='l'
                hideIcon
                width='100%'
                onClick={handleBookNow}
                isDisabled={isBookNowDisabled}
                className='flex-1 shrink-0 tabletH:min-w-[300px]'
              >
                {primaryButtonText}
              </BaseButton>
              <BaseButton
                type='text'
                size='s'
                className={`min-w-0 rounded-none border-0 bg-transparent hover:bg-transparent overflow-clip p-0 relative shrink-0 size-[32px] tabletH:size-[40px] tabletH:p-[3px] [&>span]:h-full ${
                  isCartDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
                aria-label={
                  isCartDisabled
                    ? 'Shopping bag unavailable'
                    : isInBag
                      ? 'Go to shopping bag'
                      : 'Add to shopping bag'
                }
                isDisabled={isCartDisabled}
                onClick={isCartDisabled ? undefined : handleShoppingBagClick}
                icon={
                  isCartDisabled ? (
                    <ShoppingBagIcon32 className='w-full h-full text-grayscale-gray03' />
                  ) : isInBag ? (
                    <ShoppingBagFilledIcon32 className='w-full h-full text-text-primary' />
                  ) : (
                    <ShoppingBagIcon32 className='w-full h-full' />
                  )
                }
              />
              <BaseButton
                type='text'
                size='s'
                className='min-w-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent overflow-clip relative shrink-0 size-[32px] tabletH:size-[40px] [&>span]:h-full'
                onClick={handleWishlistToggle}
                aria-label={
                  isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'
                }
                icon={
                  isWishlisted ? (
                    <HeartFilledIcon32 className='w-full h-full text-icon-failed' />
                  ) : (
                    <HeartOutlineIcon32 className='w-full h-full' />
                  )
                }
              />
            </div>
          </div>
        </div>

        {/* Image Gallery (Desktop) */}
        <div className='hidden tabletH:order-1 tabletH:flex w-full flex-col gap-[8px] items-start pb-[24px] relative shrink-0 tabletH:flex-[1_0_0] tabletH:gap-[24px] tabletH:max-w-[719px] tabletH:min-h-px tabletH:min-w-[421px] tabletH:pb-0'>
          <ImageGallery images={workshop.images} />
        </div>
      </div>

      {/* Tab Section */}
      <TabSection
        keywords={workshop.keywords}
        details={workshop.details}
        notice={workshop.notice}
        inquiry={workshop.inquiry}
        refundPolicy={workshop.refundPolicy}
        workshopId={workshop.id}
        onNoticeCreated={workshop.onNoticeCreated}
        reviewSummary={reviewSummary}
        reviewsLoading={reviewsLoading}
      />

      {/* Where we'll meet */}
      <LocationSection meetingPoint={workshop.meetingPoint} />

      {/* MVP Section */}
      <MVPSection mvps={workshop.mvps} />

      {/* Things to know */}
      <ThingsToKnowSection content={workshop.thingsToKnow} />

      {/* Sticky Booking Bar */}
      {showStickyBar &&
        (() => {
          const displayDateSlots =
            displayDateSlotsWithStatus && displayDateSlotsWithStatus.length > 0
              ? displayDateSlotsWithStatus
              : [];
          const selectedSlot = displayDateSlots.find(
            (slot) => slot.id === selectedDateSlot,
          );

          return (
            <StickyBookingBar
              data={workshop}
              title={workshop.title}
              dateSlot={
                selectedSlot
                  ? { date: selectedSlot.date, time: selectedSlot.time }
                  : null
              }
              availability={getAvailability()}
              price={formattedPrice}
              currency={workshop.currency}
              isWishlisted={isWishlisted}
              isInBag={isInBag}
              hasSelectedDate={!!selectedDateSlot}
              bookNowText={stickyBookNowText}
              bookNowSubText={stickyBookNowSubText}
              bookNowDisabled={isStickyBookNowDisabled}
              bookNowVariant={
                isPrimaryBooked || isPrimaryOnWaitlist ? 'text' : 'primary'
              }
              hidePrice={isPrimaryBooked || isPrimaryOnWaitlist}
              cartDisabled={isCartDisabled}
              onWishlistToggle={handleWishlistToggle}
              onBookNow={
                isPrimaryBooked
                  ? handleViewBooking
                  : isPrimaryOnWaitlist
                    ? handleViewWaitlist
                    : handleStickyBookNow
              }
              onAddToCart={handleAddToCart}
            />
          );
        })()}

      {/* Booking Conflict Modal */}
      <BookingConflictModal
        isOpen={showConflictModal}
        onClose={() => setShowConflictModal(false)}
        onContinue={handleConflictModalContinue}
      />

      {/* Added to Bag Toast */}
      <SuccessNotificationToast
        isOpen={showAddedToBagToast}
        onClose={() => setShowAddedToBagToast(false)}
        type={successToastType}
      />

      <WaitlistSuccessModal
        isOpen={showWaitlistSuccessModal}
        onClose={() => setShowWaitlistSuccessModal(false)}
      />

      {/* Already in Bag Modal */}
      <AlreadyInBagModal
        isOpen={showAlreadyInBagModal}
        onClose={() => setShowAlreadyInBagModal(false)}
      />

      {/* Item Unavailable Modal */}
      <ItemUnavailableModal
        isOpen={showItemUnavailableModal}
        onClose={() => setShowItemUnavailableModal(false)}
      />

      {/* Date Selection Modal */}
      <DateSelectionModal
        isOpen={showDateModal}
        onClose={() => setShowDateModal(false)}
        dateSlots={displayDateSlotsWithStatus}
        hasExistingBooking={hasBookedWorkshop}
        isBlocked={isBlocked}
        onDateSelected={handleDateSelected}
        workshop={workshop}
      />

      {/* Login Required Modal */}
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onView={() => {
          trackCtaClick('signup_modal_view', {
            trigger_source: 'kpathport_workshop_detail_cta',
            page_path: window.location.pathname,
          });
        }}
        onMethodSelect={(method) => {
          trackCtaClick('signup_method_select', {
            method,
            trigger_source: 'kpathport_workshop_detail_cta',
          });
        }}
      />

      <Modal
        isOpen={showDeleteWorkshopModal}
        type='criticalError'
        title='Delete workshop?'
        subText='This workshop will be removed from the public list and all of its booking dates will be closed.'
        leftButtonLabel='Keep workshop'
        rightButtonLabel='Delete'
        leftHideIcon
        rightHideIcon
        rightButtonLoading={isDeletingWorkshop}
        onClickLeftBtn={() => setShowDeleteWorkshopModal(false)}
        onClickRightBtn={handleDeleteWorkshop}
      />
    </div>
  );
};

export default WorkshopDetail;
